import { mergeWith } from 'lodash';
import {
  isScorable,
  makeRollupWeightFn,
  rollupWeightedScores,
} from 'js/utils/question-set';
import {
  COMBINED_CHART_PALETTE_INDEX,
  cohortChartPaletteIndex,
} from 'js/utils/color';
import standardCohortKeys from 'js/utils/cohort-keys';
import { isReservedCohortKey } from 'js/utils/assessment-cohorts';

export const ASSESSMENT_RADAR_SERIES_LIMIT = 4;
export const CURRENT_RADAR_SERIES_LABEL = 'Current';
export { ASSESSMENT_SERIES_PALETTE } from 'js/components/Charts/SeriesRadarChart/series-radar-utils';

function defaultAssessmentRadarLabel(insertedAt, fallback) {
  const date =
    insertedAt instanceof Date ? insertedAt : new Date(insertedAt ?? NaN);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function questionSetScoreSnapshot(questionSets) {
  const snapshot = {};
  Object.values(questionSets ?? {}).forEach((qs) => {
    Object.values(qs.questions ?? {}).forEach((question) => {
      if (
        question.normalizedScore == null ||
        Number.isNaN(question.normalizedScore)
      ) {
        return;
      }
      snapshot[`${qs.key}:${question.key}`] = question.normalizedScore;
    });
  });
  return snapshot;
}

export function questionSetScoresEqual(left, right) {
  const a = questionSetScoreSnapshot(left);
  const b = questionSetScoreSnapshot(right);
  const keys = Object.keys(a);
  if (keys.length === 0 || keys.length !== Object.keys(b).length) {
    return false;
  }
  return keys.every((key) => a[key] === b[key]);
}

export function questionSetsHaveResponses(questionSets) {
  const questions = Object.values(questionSets ?? {}).flatMap((qs) =>
    Object.values(qs.questions ?? {}).filter(isScorable)
  );
  if (questions.length === 0) {
    return false;
  }
  const tracked = questions.some((q) => q.responseCount != null);
  if (!tracked) {
    return questions.some((q) => q.normalizedScore != null);
  }
  return questions.some((q) => (q.responseCount ?? 0) > 0);
}

/** True when any team’s cohort slice has a finite question score. */
export function cohortSliceHasNumericScores(teamScores, cohortKey) {
  return (teamScores ?? []).some((team) =>
    (team.by_cohort?.[cohortKey] ?? []).some((row) => {
      const score = row?.score;
      return score != null && Number.isFinite(Number(score));
    })
  );
}

/** Drop empty (None)/(Other) from a radar legend; empty named cohorts stay. */
export function omitEmptyNoneFromRadarLegend(legendFields, seriesDataMaps) {
  return (legendFields ?? []).filter((field) => {
    if (!isReservedCohortKey(field.key)) {
      return true;
    }
    return questionSetsHaveResponses(seriesDataMaps?.[field.key]?.questionSets);
  });
}

/**
 * Keep empty named cohorts in the legend as disabled (“No responses”).
 * Combined and reserved (None)/(Other) are left unchanged.
 */
export function disableEmptyNamedRadarLegendFields(
  legendFields,
  seriesDataMaps
) {
  return (legendFields ?? []).map((field) => {
    if (
      field.disabled ||
      field.key === standardCohortKeys.CUMULATIVE ||
      isReservedCohortKey(field.key)
    ) {
      return field;
    }
    if (questionSetsHaveResponses(seriesDataMaps?.[field.key]?.questionSets)) {
      return field;
    }
    return { ...field, disabled: true };
  });
}

function mergeQuestionMaps(currentData, questionData) {
  return mergeWith(currentData, questionData, (orig, source) => {
    if (source === undefined) {
      return orig;
    }
    if (orig === undefined) {
      return source;
    }
    if (
      orig.assessmentInsertedAt > source.assessmentInsertedAt &&
      orig.responseCount + orig.naCount > 0
    ) {
      return orig;
    }
    return source;
  });
}

/** Latest-wins merge of question-set maps (same rule as getSummaryMetrics). */
export function mergeQuestionSetsLatestWins(questionSetMaps = []) {
  const merged = {};

  questionSetMaps.forEach((questionSets) => {
    Object.values(questionSets ?? {}).forEach((qs) => {
      if (!qs?.key) {
        return;
      }
      const orig = merged[qs.key] || {
        lastRun: null,
        displayName: qs.displayName,
        questions: {},
      };
      const questions = mergeQuestionMaps(orig.questions, qs.questions);
      merged[qs.key] = {
        ...orig,
        ...qs,
        questions,
        lastRun: qs.lastRun > orig.lastRun ? qs.lastRun : orig.lastRun,
        normalizedScore: null,
      };
    });
  });

  Object.values(merged).forEach((qs) => {
    const items = Object.values(qs.questions ?? {});
    qs.normalizedScore = rollupWeightedScores(items, {
      getScore: (q) => q.normalizedScore,
      getWeight: makeRollupWeightFn(items),
    });
  });

  return merged;
}

/**
 * Radar series for assessment snapshots (newest first).
 * Pass `{ includeCombined, combinedQuestionSets, limit }` to cap dated series
 * at build time. Assessment radars now pass all series and filter in the chart.
 * Combined/Current is omitted when its scores match the latest dated run.
 * Pass `includeEmptyAsDisabled` to keep dated runs with no scores in the legend.
 */
export function buildAssessmentRadarSeries(dataMap, assessments, options = {}) {
  const {
    includeCombined = false,
    combinedQuestionSets = null,
    combinedName = CURRENT_RADAR_SERIES_LABEL,
    limit = null,
    includeEmptyAsDisabled = false,
  } = options;

  const seriesDataMaps = {};
  const legendFields = [];

  const dated = assessments ?? [];
  const capped = limit == null ? dated : dated.slice(0, limit);
  const latestDatedQuestionSets = capped
    .map((assessment) => dataMap?.assessments?.[assessment.id]?.questionSets)
    .find(
      (questionSets) => questionSets && questionSetsHaveResponses(questionSets)
    );
  const combinedDuplicatesLatest =
    latestDatedQuestionSets &&
    questionSetScoresEqual(combinedQuestionSets, latestDatedQuestionSets);

  if (
    includeCombined &&
    combinedQuestionSets &&
    questionSetsHaveResponses(combinedQuestionSets) &&
    !combinedDuplicatesLatest
  ) {
    seriesDataMaps[standardCohortKeys.CUMULATIVE] = {
      questionSets: combinedQuestionSets,
    };
    legendFields.push({
      key: standardCohortKeys.CUMULATIVE,
      paletteIndex: COMBINED_CHART_PALETTE_INDEX,
      name: combinedName,
    });
  }
  let datedIndex = 0;

  capped.forEach((assessment) => {
    const questionSets = dataMap?.assessments?.[assessment.id]?.questionSets;
    const hasResponses = questionSetsHaveResponses(questionSets);
    if (!questionSets) {
      return;
    }
    if (!hasResponses && !includeEmptyAsDisabled) {
      return;
    }
    const key = String(assessment.id);
    seriesDataMaps[key] = { questionSets };
    legendFields.push({
      key,
      paletteIndex: cohortChartPaletteIndex(datedIndex),
      name:
        assessment.label ||
        defaultAssessmentRadarLabel(
          assessment.inserted_at,
          assessment.name || key
        ),
      disabled: !hasResponses,
    });
    datedIndex += 1;
  });

  return { legendFields, seriesDataMaps };
}
